import React, { useState } from 'react';
import { TextField, Button, Grid, Typography, MenuItem, FormControlLabel, Checkbox } from '@mui/material';
import LoadingSpinner from '../common/LoadingSpinner';
import BaseForm from './BaseForm';
import SectionDivider from '../SectionDivider';

const AdmissionInquiryForm = () => {
    const [formData, setFormData] = useState({
        howDidYouHearAboutUs: '',
        // First Parent
        parent1FirstName: '',
        parent1MiddleName: '',
        parent1LastName: '',
        parent1Salutation: '',
        parent1Email: '',
        parent1ConfirmEmail: '',
        parent1Phone: '',
        parent1Gender: '',
        // Second Parent
        hasSecondParent: true,
        parent2FirstName: '',
        parent2MiddleName: '',
        parent2LastName: '',
        parent2Salutation: '',
        parent2Email: '',
        parent2ConfirmEmail: '',
        parent2Phone: '',
        parent2Gender: '',
        // Address
        streetAddress: '',
        suiteNumber: '',
        city: '',
        state: '',
        zipCode: '',
        // Student
        studentFirstName: '',
        studentMiddleName: '',
        studentLastName: '',
        dateOfBirth: '',
        studentGender: '',
        gradeLevel: '',
        schoolYear: '',
        currentSchool: '',
        hasAnotherStudent: false
    });

    const [errors, setErrors] = useState({
        howDidYouHearAboutUs: '',
        // First Parent
        parent1FirstName: '',
        parent1MiddleName: '',
        parent1LastName: '',
        parent1Salutation: '',
        parent1Email: '',
        parent1ConfirmEmail: '',
        parent1Phone: '',
        parent1Gender: '',
        // Second Parent
        hasSecondParent: '',
        parent2FirstName: '',
        parent2MiddleName: '',
        parent2LastName: '',
        parent2Salutation: '',
        parent2Email: '',
        parent2ConfirmEmail: '',
        parent2Phone: '',
        parent2Gender: '',
        // Address
        streetAddress: '',
        suiteNumber: '',
        city: '',
        state: '',
        zipCode: '',
        // Student
        studentFirstName: '',
        studentMiddleName: '',
        studentLastName: '',
        dateOfBirth: '',
        studentGender: '',
        gradeLevel: '',
        schoolYear: '',
        currentSchool: ''
    });

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validateField = (name, value) => {
        switch (name) {
            case 'howDidYouHearAboutUs':
                return value.trim() === '' ? 'This field is required' : '';
            // First Parent
            case 'parent1FirstName':
                return value.trim() === '' ? 'First name is required' : '';
            case 'parent1MiddleName':
                return ''; // Optional
            case 'parent1LastName':
                return value.trim() === '' ? 'Last name is required' : '';
            case 'parent1Salutation':
                return ''; // Optional
            case 'parent1Email':
                if (value.trim() === '') return 'Email is required';
                return !validateEmail(value) ? 'Invalid email format' : '';
            case 'parent1ConfirmEmail':
                if (value.trim() === '') return 'Email confirmation is required';
                return value !== formData.parent1Email ? 'Emails do not match' : '';
            case 'parent1Phone':
                return ''; // Optional
            case 'parent1Gender':
                return value.trim() === '' ? 'Gender is required' : '';
            // Second Parent
            case 'parent2FirstName':
                return formData.hasSecondParent && value.trim() === '' ? 'First name is required' : '';
            case 'parent2MiddleName':
                return ''; // Optional
            case 'parent2LastName':
                return formData.hasSecondParent && value.trim() === '' ? 'Last name is required' : '';
            case 'parent2Salutation':
                return ''; // Optional
            case 'parent2Email':
                if (!formData.hasSecondParent) return '';
                if (value.trim() === '') return 'Email is required';
                return !validateEmail(value) ? 'Invalid email format' : '';
            case 'parent2ConfirmEmail':
                if (!formData.hasSecondParent) return '';
                if (value.trim() === '') return 'Email confirmation is required';
                return value !== formData.parent2Email ? 'Emails do not match' : '';
            case 'parent2Phone':
                return ''; // Optional
            case 'parent2Gender':
                return formData.hasSecondParent && value.trim() === '' ? 'Gender is required' : '';
            // Address
            case 'streetAddress':
                return value.trim() === '' ? 'Street address is required' : '';
            case 'suiteNumber':
                return ''; // Optional
            case 'city':
                return value.trim() === '' ? 'City is required' : '';
            case 'state':
                return value.trim() === '' ? 'State is required' : '';
            case 'zipCode':
                return value.trim() === '' ? 'Zip code is required' : '';
            // Student
            case 'studentFirstName':
                return value.trim() === '' ? 'First name is required' : '';
            case 'studentMiddleName':
                return ''; // Optional
            case 'studentLastName':
                return value.trim() === '' ? 'Last name is required' : '';
            case 'dateOfBirth':
                return value.trim() === '' ? 'Date of birth is required' : '';
            case 'studentGender':
                return value.trim() === '' ? 'Gender is required' : '';
            case 'gradeLevel':
                return value.trim() === '' ? 'Grade level is required' : '';
            case 'schoolYear':
                return value.trim() === '' ? 'School year is required' : '';
            case 'currentSchool':
                return value.trim() === '' ? 'Current school is required' : '';
            default:
                return '';
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        
        setFormData(prev => ({
            ...prev,
            [name]: newValue
        }));

        // If unchecking hasSecondParent, clear all second parent fields
        if (name === 'hasSecondParent' && !checked) {
            setFormData(prev => ({
                ...prev,
                parent2FirstName: '',
                parent2MiddleName: '',
                parent2LastName: '',
                parent2Salutation: '',
                parent2Email: '',
                parent2ConfirmEmail: '',
                parent2Phone: '',
                parent2Gender: ''
            }));
        }

        // Clear error when user starts typing
        setErrors(prev => ({
            ...prev,
            [name]: ''
        }));
    };

    const handleSubmit = async (token) => {
        // Validate all fields
        const newErrors = {
            howDidYouHearAboutUs: validateField('howDidYouHearAboutUs', formData.howDidYouHearAboutUs),
            // First Parent
            parent1FirstName: validateField('parent1FirstName', formData.parent1FirstName),
            parent1MiddleName: validateField('parent1MiddleName', formData.parent1MiddleName),
            parent1LastName: validateField('parent1LastName', formData.parent1LastName),
            parent1Salutation: validateField('parent1Salutation', formData.parent1Salutation),
            parent1Email: validateField('parent1Email', formData.parent1Email),
            parent1ConfirmEmail: validateField('parent1ConfirmEmail', formData.parent1ConfirmEmail),
            parent1Phone: validateField('parent1Phone', formData.parent1Phone),
            parent1Gender: validateField('parent1Gender', formData.parent1Gender),
            // Second Parent
            parent2FirstName: validateField('parent2FirstName', formData.parent2FirstName),
            parent2MiddleName: validateField('parent2MiddleName', formData.parent2MiddleName),
            parent2LastName: validateField('parent2LastName', formData.parent2LastName),
            parent2Salutation: validateField('parent2Salutation', formData.parent2Salutation),
            parent2Email: validateField('parent2Email', formData.parent2Email),
            parent2ConfirmEmail: validateField('parent2ConfirmEmail', formData.parent2ConfirmEmail),
            parent2Phone: validateField('parent2Phone', formData.parent2Phone),
            parent2Gender: validateField('parent2Gender', formData.parent2Gender),
            // Address
            streetAddress: validateField('streetAddress', formData.streetAddress),
            suiteNumber: validateField('suiteNumber', formData.suiteNumber),
            city: validateField('city', formData.city),
            state: validateField('state', formData.state),
            zipCode: validateField('zipCode', formData.zipCode),
            // Student
            studentFirstName: validateField('studentFirstName', formData.studentFirstName),
            studentMiddleName: validateField('studentMiddleName', formData.studentMiddleName),
            studentLastName: validateField('studentLastName', formData.studentLastName),
            dateOfBirth: validateField('dateOfBirth', formData.dateOfBirth),
            studentGender: validateField('studentGender', formData.studentGender),
            gradeLevel: validateField('gradeLevel', formData.gradeLevel),
            schoolYear: validateField('schoolYear', formData.schoolYear),
            currentSchool: validateField('currentSchool', formData.currentSchool)
        };

        setErrors(newErrors);

        // Check if there are any errors
        const hasErrors = Object.values(newErrors).some(error => error !== '');

        if (hasErrors) {
            return {
                success: false,
                validationError: true
            };
        }

        const response = await fetch(`${process.env.PUBLIC_URL}/send-email.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                ...formData,
                formType: 'admission',
                captchaToken: token
            })
        });

        const result = await response.json();
        
        if (result.success) {
            // Reset form based on hasAnotherStudent value
            if (formData.hasAnotherStudent) {
                // Only clear student information
                setFormData(prev => ({
                    ...prev,
                    studentFirstName: '',
                    studentMiddleName: '',
                    studentLastName: '',
                    dateOfBirth: '',
                    studentGender: '',
                    gradeLevel: '',
                    schoolYear: '',
                    currentSchool: '',
                    hasAnotherStudent: false
                }));
            } else {
                // Clear entire form
                setFormData({
                    howDidYouHearAboutUs: '',
                    // First Parent
                    parent1FirstName: '',
                    parent1MiddleName: '',
                    parent1LastName: '',
                    parent1Salutation: '',
                    parent1Email: '',
                    parent1ConfirmEmail: '',
                    parent1Phone: '',
                    parent1Gender: '',
                    // Second Parent
                    hasSecondParent: true,
                    parent2FirstName: '',
                    parent2MiddleName: '',
                    parent2LastName: '',
                    parent2Salutation: '',
                    parent2Email: '',
                    parent2ConfirmEmail: '',
                    parent2Phone: '',
                    parent2Gender: '',
                    // Address
                    streetAddress: '',
                    suiteNumber: '',
                    city: '',
                    state: '',
                    zipCode: '',
                    // Student
                    studentFirstName: '',
                    studentMiddleName: '',
                    studentLastName: '',
                    dateOfBirth: '',
                    studentGender: '',
                    gradeLevel: '',
                    schoolYear: '',
                    currentSchool: '',
                    hasAnotherStudent: false
                });
            }
        }
        
        return result;
    };

    return (
        <BaseForm
            onSubmit={handleSubmit}
            title="Thank you for your interest in MacGillis Global Academy!"
            subtitle="Please fill out the form below and our team will contact you shortly."
        >
            {({ isSubmitting }) => (
                <>
                    <SectionDivider sx={{mb:4}} />
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                fullWidth
                                label="How did you hear about us?"
                                name="howDidYouHearAboutUs"
                                value={formData.howDidYouHearAboutUs}
                                onChange={handleChange}
                                required
                                error={!!errors.howDidYouHearAboutUs}
                                helperText={errors.howDidYouHearAboutUs}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>

                    {/* First Parent Section */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        1. First Parent / Guardian Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="parent1FirstName"
                                value={formData.parent1FirstName}
                                onChange={handleChange}
                                required
                                error={!!errors.parent1FirstName}
                                helperText={errors.parent1FirstName}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                fullWidth
                                label="Middle Name"
                                name="parent1MiddleName"
                                value={formData.parent1MiddleName}
                                onChange={handleChange}
                                error={!!errors.parent1MiddleName}
                                helperText={errors.parent1MiddleName}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="parent1LastName"
                                value={formData.parent1LastName}
                                onChange={handleChange}
                                required
                                error={!!errors.parent1LastName}
                                helperText={errors.parent1LastName}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                            <TextField
                                fullWidth
                                label="Salutation"
                                name="parent1Salutation"
                                value={formData.parent1Salutation}
                                onChange={handleChange}
                                error={!!errors.parent1Salutation}
                                helperText={errors.parent1Salutation}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Email Address"
                                name="parent1Email"
                                type="email"
                                value={formData.parent1Email}
                                onChange={handleChange}
                                required
                                error={!!errors.parent1Email}
                                helperText={errors.parent1Email}
                                disabled={isSubmitting}
                                slotProps={{
                                    input: {
                                        'aria-label': 'Email address',
                                        'data-testid': 'parent1-email-input'
                                    }
                                }}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Confirm Email Address"
                                name="parent1ConfirmEmail"
                                type="email"
                                value={formData.parent1ConfirmEmail}
                                onChange={handleChange}
                                required
                                error={!!errors.parent1ConfirmEmail}
                                helperText={errors.parent1ConfirmEmail}
                                disabled={isSubmitting}
                                slotProps={{
                                    input: {
                                        'aria-label': 'Confirm email address',
                                        'data-testid': 'parent1-confirm-email-input'
                                    }
                                }}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                name="parent1Phone"
                                value={formData.parent1Phone}
                                onChange={handleChange}
                                error={!!errors.parent1Phone}
                                helperText={errors.parent1Phone}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                            <TextField
                                fullWidth
                                select
                                label="Gender"
                                name="parent1Gender"
                                value={formData.parent1Gender}
                                onChange={handleChange}
                                required
                                error={!!errors.parent1Gender}
                                helperText={errors.parent1Gender}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            >
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                                <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>

                    {/* Second Parent Section */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        2. Second Parent / Guardian Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.hasSecondParent}
                                        onChange={handleChange}
                                        name="hasSecondParent"
                                        color="primary"
                                    />
                                }
                                label="Student has a second parent/guardian"
                            />
                        </Grid>
                    </Grid>
                    {formData.hasSecondParent && (
                        <>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="First Name"
                                        name="parent2FirstName"
                                        value={formData.parent2FirstName}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.parent2FirstName}
                                        helperText={errors.parent2FirstName}
                                        disabled={isSubmitting}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                    <TextField
                                        fullWidth
                                        label="Middle Name"
                                        name="parent2MiddleName"
                                        value={formData.parent2MiddleName}
                                        onChange={handleChange}
                                        error={!!errors.parent2MiddleName}
                                        helperText={errors.parent2MiddleName}
                                        disabled={isSubmitting}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 8, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        label="Last Name"
                                        name="parent2LastName"
                                        value={formData.parent2LastName}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.parent2LastName}
                                        helperText={errors.parent2LastName}
                                        disabled={isSubmitting}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 4, md: 2 }}>
                                    <TextField
                                        fullWidth
                                        label="Salutation"
                                        name="parent2Salutation"
                                        value={formData.parent2Salutation}
                                        onChange={handleChange}
                                        error={!!errors.parent2Salutation}
                                        helperText={errors.parent2Salutation}
                                        disabled={isSubmitting}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        name="parent2Email"
                                        type="email"
                                        value={formData.parent2Email}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.parent2Email}
                                        helperText={errors.parent2Email}
                                        disabled={isSubmitting}
                                        slotProps={{
                                            input: {
                                                'aria-label': 'Email address',
                                                'data-testid': 'parent2-email-input'
                                            }
                                        }}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Confirm Email Address"
                                        name="parent2ConfirmEmail"
                                        type="email"
                                        value={formData.parent2ConfirmEmail}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.parent2ConfirmEmail}
                                        helperText={errors.parent2ConfirmEmail}
                                        disabled={isSubmitting}
                                        slotProps={{
                                            input: {
                                                'aria-label': 'Confirm email address',
                                                'data-testid': 'parent2-confirm-email-input'
                                            }
                                        }}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        name="parent2Phone"
                                        value={formData.parent2Phone}
                                        onChange={handleChange}
                                        error={!!errors.parent2Phone}
                                        helperText={errors.parent2Phone}
                                        disabled={isSubmitting}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                    <TextField
                                        fullWidth
                                        select
                                        label="Gender"
                                        name="parent2Gender"
                                        value={formData.parent2Gender}
                                        onChange={handleChange}
                                        required
                                        error={!!errors.parent2Gender}
                                        helperText={errors.parent2Gender}
                                        disabled={isSubmitting}
                                        sx={{ width: '100%', boxSizing: 'border-box' }}
                                    >
                                        <MenuItem value="male">Male</MenuItem>
                                        <MenuItem value="female">Female</MenuItem>
                                        <MenuItem value="other">Other</MenuItem>
                                        <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                                    </TextField>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    {/* Third Section */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        3. Home Address
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 8, md: 8 }}>
                            <TextField
                                fullWidth
                                label="Street Address"
                                name="streetAddress"
                                value={formData.streetAddress}
                                onChange={handleChange}
                                required
                                error={!!errors.streetAddress}
                                helperText={errors.streetAddress}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 4, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Suite / Apartment #"
                                name="suiteNumber"
                                value={formData.suiteNumber}
                                onChange={handleChange}
                                error={!!errors.suiteNumber}
                                helperText={errors.suiteNumber}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="City"
                                name="city"
                                value={formData.city}
                                onChange={handleChange}
                                required
                                error={!!errors.city}
                                helperText={errors.city}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="State"
                                name="state"
                                value={formData.state}
                                onChange={handleChange}
                                required
                                error={!!errors.state}
                                helperText={errors.state}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Zip Code"
                                name="zipCode"
                                value={formData.zipCode}
                                onChange={handleChange}
                                required
                                error={!!errors.zipCode}
                                helperText={errors.zipCode}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>

                    {/* Fourth Section */}
                    <Typography variant="h6" sx={{ mt: 3, mb: 1 }}>
                        4. Student Information
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="First Name"
                                name="studentFirstName"
                                value={formData.studentFirstName}
                                onChange={handleChange}
                                required
                                error={!!errors.studentFirstName}
                                helperText={errors.studentFirstName}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Middle Name"
                                name="studentMiddleName"
                                value={formData.studentMiddleName}
                                onChange={handleChange}
                                error={!!errors.studentMiddleName}
                                helperText={errors.studentMiddleName}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 12, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                name="studentLastName"
                                value={formData.studentLastName}
                                onChange={handleChange}
                                required
                                error={!!errors.studentLastName}
                                helperText={errors.studentLastName}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Date of Birth"
                                name="dateOfBirth"
                                type="date"
                                value={formData.dateOfBirth}
                                onChange={handleChange}
                                required
                                error={!!errors.dateOfBirth}
                                helperText={errors.dateOfBirth}
                                disabled={isSubmitting}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                select
                                label="Gender"
                                name="studentGender"
                                value={formData.studentGender}
                                onChange={handleChange}
                                required
                                error={!!errors.studentGender}
                                helperText={errors.studentGender}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            >
                                <MenuItem value="male">Male</MenuItem>
                                <MenuItem value="female">Female</MenuItem>
                                <MenuItem value="other">Other</MenuItem>
                                <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                            </TextField>
                        </Grid>
                    </Grid>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Grade Level of Interest"
                                name="gradeLevel"
                                value={formData.gradeLevel}
                                onChange={handleChange}
                                required
                                error={!!errors.gradeLevel}
                                helperText={errors.gradeLevel}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="School Year"
                                name="schoolYear"
                                value={formData.schoolYear}
                                onChange={handleChange}
                                required
                                error={!!errors.schoolYear}
                                helperText={errors.schoolYear}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                            <TextField
                                fullWidth
                                label="Current School"
                                name="currentSchool"
                                value={formData.currentSchool}
                                onChange={handleChange}
                                required
                                error={!!errors.currentSchool}
                                helperText={errors.currentSchool}
                                disabled={isSubmitting}
                                sx={{ width: '100%', boxSizing: 'border-box' }}
                            />
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.hasAnotherStudent}
                                        onChange={handleChange}
                                        name="hasAnotherStudent"
                                        color="primary"
                                    />
                                }
                                label="Do you have another student to add?"
                            />
                        </Grid>
                    </Grid>

                    <Button
                        type="submit"
                        variant="contained"
                        color="secondary"
                        disabled={isSubmitting}
                        sx={{ mt: 2 }}
                    >
                        {isSubmitting ? <LoadingSpinner size={20} /> : 'Submit Inquiry'}
                    </Button>
                </>
            )}
        </BaseForm>
    );
};

export default AdmissionInquiryForm; 